import {
  ApplyConditionPayload,
  IDentalChart,
  IDentalData,
} from '@dataTypes/chartingFeature.types'
import { applyConditionPayloadsToDentalData } from '@utils/dental-condition-application.utils'
import { dentalConditionToothRestrictionMessage } from '@constants/Charting/conditions.schema'
import { getAvailableFurcationAreas } from '@components/pages/Charting/utils/condition.utils'
import { hasCondition } from '@utils/dental-data.utils'
import { DentalVoiceOperation } from '@utils/dental-transcript.utils'

export type DentalVoiceCursor = {
  tooth: number
}

export type DentalKeywordState = {
  cursor: DentalVoiceCursor
  paused: boolean
  history: Array<{
    cursor: DentalVoiceCursor
    dental_data: IDentalData[]
  }>
}

export const createDentalKeywordState = (
  cursor: DentalVoiceCursor = { tooth: 1 },
): DentalKeywordState => ({
  cursor: { ...cursor },
  paused: false,
  history: [],
})

const clampTooth = (tooth: number): number =>
  Math.min(32, Math.max(1, Math.round(tooth)))

const toPayloads = (
  op: Extract<DentalVoiceOperation, { op: 'apply' }>,
  tooth: number,
): ApplyConditionPayload[] => {
  const type = op.type?.toLowerCase() ?? null
  const material = op.material?.toLowerCase() ?? null
  const surfaces = op.surfaces ?? []

  // Match side panel: furcation type defaults to all available areas on that tooth.
  if (op.condition === 'furcation' && !op.clear) {
    const areas =
      surfaces.length > 0 ? surfaces : getAvailableFurcationAreas(tooth)
    if (areas.length) {
      return [
        {
          condition: 'furcation',
          type,
          material: null,
          surface: areas.join(','),
        },
      ]
    }
  }

  if (
    (op.condition === 'filling' ||
      op.condition === 'inlay' ||
      op.condition === 'onlay') &&
    material &&
    surfaces.length
  ) {
    return [
      { condition: op.condition, type: null, material, surface: null },
      ...surfaces.map((surface) => ({
        condition: op.condition,
        type: null,
        material: null,
        surface,
      })),
    ]
  }

  if (
    (op.condition === 'caries' || op.condition === 'recurring_caries') &&
    surfaces.length
  ) {
    return surfaces.map((surface) => ({
      condition: op.condition,
      type,
      material: null,
      surface,
    }))
  }

  if (surfaces.length) {
    return surfaces.map((surface) => ({
      condition: op.condition,
      type,
      material,
      surface,
    }))
  }

  return [
    {
      condition: op.condition,
      type,
      material,
      surface: null,
    },
  ]
}

const cloneDeep = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

const snapshotData = (chart: IDentalChart): IDentalData[] =>
  cloneDeep(chart.dental_data ?? [])

export function applyDentalVoiceOperations(
  current: IDentalChart,
  previous: DentalKeywordState,
  input: DentalVoiceOperation[],
): {
  chart: IDentalChart
  state: DentalKeywordState
  message: string
  control?: 'stop'
  toothChanged?: number
} {
  if (!Array.isArray(input) || input.length > 64)
    throw new Error('Invalid voice response.')

  const state: DentalKeywordState = cloneDeep(previous)
  let chart: IDentalChart = {
    ...current,
    dental_data: snapshotData(current),
  }
  const notes: string[] = []
  let control: 'stop' | undefined
  let toothChanged: number | undefined
  let chartDirty = false

  const ensureListening = (): void => {
    if (state.paused)
      throw new Error(
        'Entry is paused. Say “resume” before applying conditions.',
      )
  }

  for (const operation of input) {
    switch (operation.op) {
      case 'stop':
        control = 'stop'
        notes.push('Microphone stopped.')
        break
      case 'pause':
        state.paused = true
        notes.push('Paused. Say “resume” to continue.')
        break
      case 'resume':
        state.paused = false
        notes.push(`Resumed at tooth ${state.cursor.tooth}.`)
        break
      case 'clear_pending':
        notes.push('Nothing pending to clear.')
        break
      case 'undo': {
        const prior = state.history.pop()
        if (!prior)
          throw new Error('Nothing to undo in this microphone session.')
        chart = { ...chart, dental_data: cloneDeep(prior.dental_data) }
        state.cursor = { ...prior.cursor }
        toothChanged = state.cursor.tooth
        chartDirty = true
        notes.push(`Undid last voice change. Tooth ${state.cursor.tooth}.`)
        break
      }
      case 'next': {
        ensureListening()
        if (state.cursor.tooth >= 32) throw new Error('Already at tooth 32.')
        state.cursor.tooth = clampTooth(state.cursor.tooth + 1)
        toothChanged = state.cursor.tooth
        notes.push(`Tooth ${state.cursor.tooth}.`)
        break
      }
      case 'back': {
        ensureListening()
        if (state.cursor.tooth <= 1) throw new Error('Already at tooth 1.')
        state.cursor.tooth = clampTooth(state.cursor.tooth - 1)
        toothChanged = state.cursor.tooth
        notes.push(`Tooth ${state.cursor.tooth}.`)
        break
      }
      case 'jump': {
        ensureListening()
        state.cursor.tooth = clampTooth(operation.tooth)
        toothChanged = state.cursor.tooth
        notes.push(`Selected tooth ${state.cursor.tooth}.`)
        break
      }
      case 'apply': {
        ensureListening()
        const tooth = clampTooth(operation.tooth ?? state.cursor.tooth)
        if (!operation.clear) {
          const restriction = dentalConditionToothRestrictionMessage(
            operation.condition,
            tooth,
          )
          if (restriction) throw new Error(restriction)
        }
        state.cursor.tooth = tooth
        toothChanged = tooth
        const before = snapshotData(chart)
        const payloads = toPayloads(operation, tooth)
        let dentalData = chart.dental_data ?? []
        for (const payload of payloads) {
          const exists = hasCondition(
            dentalData,
            tooth,
            payload.condition,
            payload.type,
            payload.material,
            payload.surface,
          )
          // Side panel toggles; voice is additive unless clear/remove is spoken.
          if (operation.clear ? !exists : exists) continue
          dentalData = applyConditionPayloadsToDentalData({
            dentalData,
            selectedTooth: tooth,
            conditionOrConditions: payload,
          })
        }
        chart = { ...chart, dental_data: dentalData }
        state.history.push({
          cursor: { tooth },
          dental_data: before,
        })
        if (state.history.length > 40) state.history.shift()
        chartDirty = true
        const label = operation.clear ? 'Cleared' : 'Applied'
        notes.push(
          `${label} ${operation.condition} on tooth ${tooth}${
            operation.type ? ` (${operation.type})` : ''
          }${operation.material ? ` ${operation.material}` : ''}${
            operation.surfaces?.length
              ? ` [${operation.surfaces.join(', ')}]`
              : ''
          }.`,
        )
        break
      }
      case 'unsupported':
        throw new Error(operation.reason)
      default:
        throw new Error('Unsupported voice command.')
    }
  }

  return {
    chart: chartDirty ? chart : current,
    state,
    message: notes.join(' ') || `Listening at tooth ${state.cursor.tooth}.`,
    control,
    toothChanged,
  }
}
